import React, { ReactNode } from "react";
import {
	toast,
	ToastContainer,
	ToastContainerProps,
	ToastOptions,
	Id,
	UpdateOptions,
	Slide,
	ToastClassName,
} from "react-toastify";
import styles from "./index.module.css";
import Lottie, { LottieOptions } from "lottie-react";
import infoToastIcon from "../images/info-toast-icon.json";
import successToastIcon from "../images/success-toast-icon.json";
import warningToastIcon from "../images/warning-toast-icon.json";
import errorToastIcon from "../images/error-toast-icon.json";
import "react-toastify/dist/ReactToastify.css";
import { IconButton, Typography } from "@mui/material";
import { Close } from "@mui/icons-material";
import { createPortal } from "react-dom";
import ThemeWrapper from "../../theme";
import { useMobileLayout } from "../../hooks";

export type CoreToastContainerProps = { enablePortal?: boolean } & ToastContainerProps;

type ToastSize = "small" | "large" | "auto";

type CoreToastOptions = ToastOptions & {
	title?: ReactNode;
	size?: ToastSize;
};
type CoreUpdateToastOptions = UpdateOptions & {
	title?: ReactNode;
	size?: ToastSize;
};

function ToastCloseButton({ closeToast }: { closeToast: React.MouseEventHandler<HTMLButtonElement> }) {
	return (
		<IconButton className={styles.closeButton} size="small" onClick={closeToast}>
			<Close fontSize="small" />
		</IconButton>
	);
}

export function CoreToastContainer({
	enablePortal = false,
	position = "top-center",
	closeButton = ToastCloseButton,
	transition = Slide,
	newestOnTop = true,
	className,
	...props
}: CoreToastContainerProps) {
	const isMobileLayout = useMobileLayout();
	const container = (
		<ToastContainer
			{...props}
			className={(context) => `
				${context?.defaultClassName ?? ""}
				${styles.toastContainer}
				${isMobileLayout ? styles.toastContainerCompact : ""}
				${typeof className === "function" ? className(context) : (className ?? "")}
			`}
			position={position}
			closeButton={closeButton}
			newestOnTop={newestOnTop}
			transition={transition}
		/>
	);
	return enablePortal ? createPortal(container, document.body) : container;
}

function ToastContent({ title, children }: { title: ReactNode; children: ReactNode }) {
	return (
		<ThemeWrapper>
			<div className={styles.toastContent}>
				<div className={styles.toastTextWrapper}>
					<Typography
						variant="h4"
						sx={{ color: "#222222", fontSize: "15px", fontWeight: 700, margin: 0, textAlign: "start" }}
					>
						{title}
					</Typography>
					<Typography>{children}</Typography>
				</div>
			</div>
		</ThemeWrapper>
	);
}

function ToastIcon({ animationData }: { animationData: LottieOptions["animationData"] }) {
	return <Lottie animationData={animationData} width={32} height={32} loop={false} />;
}

function getAndMergeToastClassName(
	content: ReactNode,
	{ className = "", size = "auto" }: Pick<CoreUpdateToastOptions, "className" | "size"> = {},
): ToastClassName {
	let isSmall = size !== "large";
	if (size === "auto" && content && typeof content === "string") {
		isSmall = content.length < 80;
	}
	return (...args) => {
		const originalClassName = typeof className === "function" ? className(...args) : className;
		return `${args[0]?.defaultClassName} ${styles.toastMessage} ${isSmall ? styles.small : ""} ${originalClassName}`;
	};
}

export const coreToast = {
	...toast,
	info(content: ReactNode, options?: CoreToastOptions) {
		return toast.info(<ToastContent title={options?.title ?? "Info"}>{content}</ToastContent>, {
			...(options ?? {}),
			className: getAndMergeToastClassName(content, options),
			icon: options?.icon ?? <ToastIcon animationData={infoToastIcon} />,
			closeButton: ToastCloseButton,
		});
	},
	success(content: ReactNode, options?: CoreToastOptions) {
		return toast.success(<ToastContent title={options?.title ?? "Success"}>{content}</ToastContent>, {
			...(options ?? {}),
			className: getAndMergeToastClassName(content, options),
			icon: options?.icon ?? <ToastIcon animationData={successToastIcon} />,
			closeButton: ToastCloseButton,
		});
	},
	warning(content: ReactNode, options?: CoreToastOptions) {
		return toast.warning(<ToastContent title={options?.title ?? "Warning"}>{content}</ToastContent>, {
			...(options ?? {}),
			className: getAndMergeToastClassName(content, options),
			icon: options?.icon ?? <ToastIcon animationData={warningToastIcon} />,
			closeButton: ToastCloseButton,
		});
	},
	error(content: ReactNode, options?: CoreToastOptions) {
		return toast.error(<ToastContent title={options?.title ?? "Error"}>{content}</ToastContent>, {
			...(options ?? {}),
			className: getAndMergeToastClassName(content, options),
			icon: options?.icon ?? <ToastIcon animationData={errorToastIcon} />,
			closeButton: ToastCloseButton,
		});
	},
	loading(content: ReactNode, options?: CoreToastOptions) {
		return toast.loading(<ToastContent title={options?.title ?? "Loading"}>{content}</ToastContent>, {
			...(options ?? {}),
			className: getAndMergeToastClassName(content, options),
		});
	},
	update(id: Id, content: ReactNode, type: "info" | "success" | "warning" | "error", options?: CoreUpdateToastOptions) {
		const titleType = {
			info: "Info",
			success: "Success",
			warning: "Warning",
			error: "Error",
		};
		const iconType = {
			info: <ToastIcon animationData={infoToastIcon} />,
			success: <ToastIcon animationData={successToastIcon} />,
			warning: <ToastIcon animationData={warningToastIcon} />,
			error: <ToastIcon animationData={errorToastIcon} />,
		};

		const doUpdate = () => {
			toast.update(id, {
				render: <ToastContent title={options?.title ?? titleType[type]}>{content}</ToastContent>,
				type: type,
				isLoading: false,
				draggable: true,
				closeOnClick: true,
				icon: options?.icon ?? iconType[type],
				closeButton: ToastCloseButton,
				...(options ?? {}),
				className: getAndMergeToastClassName(content, options),
			});
		};
		if (toast.isActive(id)) {
			doUpdate();
		} else {
			setTimeout(doUpdate, 200);
		}
	},
	updateOrShow(
		id: Id | undefined,
		content: ReactNode,
		type: "info" | "success" | "warning" | "error",
		options?: CoreUpdateToastOptions & CoreToastOptions,
	) {
		if (id == null) {
			switch (type) {
				case "info":
					return this.info(content, options);
				case "success":
					return this.success(content, options);
				case "warning":
					return this.warning(content, options);
				case "error":
					return this.error(content, options);
			}
		}
		return this.update(id, content, type, options);
	},
};
